/**
 * intentEngine.js — Re Leve IA (Etapa 4)
 * Classifica a intenção da mensagem sem LLM externa.
 * Determinístico, baseado em termos-chave normalizados.
 */

function normalize(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

// Intenções prioritárias (guardrails sensíveis) verificadas primeiro
const INTENT_MAP = [
  {
    intent: 'dieta_prescricao',
    priority: 1,
    terms: [
      'dieta', 'prescricao', 'plano alimentar', 'quantas calorias devo comer',
      'o que devo comer', 'cardapio personalizado', 'plano nutricional',
    ],
  },
  {
    intent: 'emagrecimento',
    priority: 1,
    terms: [
      'emagrecer', 'perder peso', 'emagrecimento', 'secar', 'dieta para emagrecer',
      'vou emagrecer', 'quero emagrecer', 'perco peso',
    ],
  },
  {
    intent: 'reclamacao',
    priority: 1,
    terms: [
      'reclamacao', 'reclamar', 'veio errado', 'atrasou', 'problema',
      'ruim', 'cancelar', 'reembolso', 'devolucao', 'reacao alergica',
      'alergia grave', 'nao gostei', 'errado', 'estragado',
    ],
  },
  {
    intent: 'desconto_alto',
    priority: 1,
    terms: [
      '30% de desconto', '40% de desconto', '50% de desconto',
      'desconto maior', 'desconto maximo', 'me da desconto', 'quero desconto',
      'grande desconto', 'muito desconto',
    ],
  },
  {
    intent: 'compra_coletiva',
    priority: 1,
    terms: [
      'empresa', 'equipe', 'escritorio', 'grupo', 'funcionarios', 'reuniao',
      'comprar para equipe', 'comprar para empresa', 'minha empresa',
      'meu time', 'para o trabalho', 'colegas',
    ],
  },
  // confirmar_kit — antes de whatsapp (Etapa 6)
  {
    intent: 'confirmar_kit',
    priority: 1,
    terms: [
      'confirmar kit', 'quero esse kit', 'pode montar', 'fechar esse',
      'vou querer', 'quero finalizar', 'confirmar pedido', 'confirmo',
      'pode confirmar', 'fecha o kit',
    ],
  },
  // registrar_refeicao — antes de registrar_calorias (Etapa 6)
  {
    intent: 'registrar_refeicao',
    priority: 1,
    terms: [
      'consumi', 'comi', 'ja almocei', 'registrar almoco', 'registrar jantar',
      'registrar lanche', 'registrar bebida', 'registrar refeicao',
      'almocei', 'jantei', 'lancei', 'registrei', 'registrei o almoco',
      'registrei o jantar', 'registrei o lanche', 'registrei a refeicao',
    ],
  },
  {
    intent: 'registrar_calorias',
    priority: 1,
    terms: [
      'caloria', 'calorias', 'registrar consumo', 'acompanhamento calorico',
      'acompanhar alimentacao', 'diario alimentar',
      'registro calorico', 'kcal',
    ],
  },
  {
    intent: 'restricao_alimentar',
    priority: 2,
    terms: [
      'sem lactose', 'sem gluten', 'low carb', 'restricao', 'alergia',
      'intolerancia', 'vegetariano', 'vegano', 'celiaco', 'sem acucar',
    ],
  },
  {
    intent: 'montar_semana',
    priority: 2,
    terms: [
      'semana', 'montar minha semana', 'rotina', 'semanal', '5 dias',
      'kit semanal', 'semana util', 'resolver minha semana', 'semana completa',
      'montar semana',
    ],
  },
  {
    intent: 'kit_dia',
    priority: 2,
    terms: [
      'hoje', 'kit dia', 'resolver meu dia', 'kit para hoje',
      'almoco', 'jantar', 'lanche', 'refeicao de hoje',
    ],
  },
  {
    intent: 'promocoes',
    priority: 2,
    terms: [
      'promocao', 'promocoes', 'oferta', 'beneficio', 'cupom',
      'tem promocao', 'ver promocoes',
    ],
  },
  {
    intent: 'indicacao',
    priority: 2,
    terms: [
      'indicar', 'indicacao', 'amigo', 'amiga', 'indique',
      'ganhar beneficio', 'indicar alguem', 'programa de indicacao',
    ],
  },
  {
    intent: 'whatsapp',
    priority: 2,
    terms: [
      'whatsapp', 'comprar', 'finalizar', 'pedido', 'fechar pedido',
      'falar com atendente', 'zap', 'falar com alguem', 'falar com voces',
    ],
  },
  {
    intent: 'recompra',
    priority: 2,
    terms: [
      'comprar de novo', 'repetir pedido', 'recomprar', 'renovar',
      'renovar kit', 'proxima semana', 'quero de novo', 'repetir a semana',
    ],
  },
  {
    intent: 'assinatura',
    priority: 2,
    terms: [
      'assinatura', 'recorrente', 'todo mes', 'toda semana',
      'programar entrega', 'recompra programada', 'entrega automatica',
    ],
  },
  {
    intent: 'cross_sell',
    priority: 2,
    terms: [
      'suco', 'snack', 'sobremesa', 'brownie', 'complemento',
      'bebida', 'algo mais', 'mais alguma coisa',
    ],
  },
  {
    intent: 'cardapio',
    priority: 2,
    terms: [
      'cardapio', 'sabores', 'pratos', 'opcoes', 'tem hoje',
      'disponivel hoje', 'o que tem', 'menu', 'o que voces tem',
    ],
  },
  {
    intent: 'saudacao',
    priority: 3,
    terms: [
      'oi', 'ola', 'bom dia', 'boa tarde', 'boa noite',
      'oi tudo bem', 'hello', 'salve', 'e ai',
    ],
  },
];

export function detectIntent(message) {
  if (!message || !message.trim()) {
    return { intent: 'fallback', confidence: 'baixa', matchedTerms: [], rawMessage: '' };
  }

  const raw = message;
  const norm = normalize(message);
  const matched = [];

  // Verificar prioridade 1 (guardrails sensíveis) primeiro
  for (const priority of [1, 2, 3]) {
    for (const entry of INTENT_MAP) {
      if (entry.priority !== priority) continue;
      const terms = entry.terms.filter(t => norm.includes(normalize(t)));
      if (terms.length > 0) {
        matched.push({ intent: entry.intent, terms, priority });
      }
    }
    if (matched.length > 0) break;
  }

  if (matched.length === 0) {
    return { intent: 'fallback', confidence: 'baixa', matchedTerms: [], rawMessage: raw };
  }

  // Pegar a intenção com mais termos matched entre as encontradas
  matched.sort((a, b) => b.terms.length - a.terms.length);
  const best = matched[0];

  const confidence = best.terms.length >= 2 ? 'alta' : best.terms.length === 1 ? 'media' : 'baixa';

  return {
    intent: best.intent,
    confidence,
    matchedTerms: best.terms,
    rawMessage: raw,
  };
}
